import React, { memo, useCallback, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Paper, Grid, Typography, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useIntl } from 'react-intl';
import { useFeatureFlag } from 'modules/common/hooks';
import PieChart from '../PieChart';
import LabelChartSkeleton from '../LabelChartSkeleton';
import { LabelNoData } from '../LabelNoData';
import { PieChartData } from '../utils';

type StyleProps = {
  show: boolean;
  isTiwVpatEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    maxHeight: theme.spacing(32.5),
    minHeight: theme.spacing(32.5),
    marginRight: theme.spacing(2),
    '&:hover $removeBtn, &:focus-within $removeBtn': {
      visibility: ({ isTiwVpatEnabled }: StyleProps) =>
        isTiwVpatEnabled ? 'visible' : 'hidden'
    }
  },
  typography: {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headingContainer: {
    minHeight: theme.spacing(5)
  },
  headingWidth: {
    maxWidth: '81%'
  },
  removeBtn: {
    visibility: ({ isTiwVpatEnabled, show }: StyleProps) => {
      if (isTiwVpatEnabled) {
        return 'hidden';
      }
      show ? 'visible' : 'hidden';
    },
    opacity: ({ show }: StyleProps) => (show ? 1 : 0),
    '-webkit-transition': 'opacity 0.5s ease-in-out',
    '-moz-transition': 'opacity 0.5s ease-in-out',
    '-ms-transition': 'opacity 0.5s ease-in-out',
    '-o-transition': 'opacity 0.5s ease-in-out',
    transition: 'opacity 0.5s ease-in-out'
  }
}));
const useTooltipClasses = makeStyles((theme) => ({
  popper: {
    marginTop: `${theme.spacing(-1)} !important`
  }
}));

type OEFChartProps = {
  labelId: string;
  labelName: string;
  handleRemove: (labelId: string) => void;
  loading: boolean;
  isEmpty: boolean;
  dataset: PieChartData;
  setGetCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
};

export const OEFChart: React.FC<OEFChartProps> = ({
  labelId,
  labelName,
  handleRemove,
  dataset,
  loading,
  isEmpty,
  setGetCurrentPage
}: OEFChartProps) => {
  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');

  const [show, setShow] = useState<boolean>(false);
  const showIcon = useCallback(() => setShow(true), []);
  const closeIcon = useCallback(() => setShow(false), []);

  const classes = useStyles({ show, isTiwVpatEnabled });
  const tooltip = useTooltipClasses();

  const handleClickRemove = useCallback(() => {
    handleRemove(labelId);
  }, [handleRemove, labelId]);

  const { formatMessage } = useIntl();
  const removeButton = formatMessage(
    { id: 'insights.timeSheetLabels.removeOEFChart' },
    { chartName: labelName }
  );

  return (
    <Paper
      elevation={3}
      data-testid={`source-by-OEF-${labelName}-chart-container`}
      className={classes.container}
      onMouseOver={showIcon}
      onMouseLeave={closeIcon}
      onFocus={isTiwVpatEnabled ? showIcon : undefined}
      tabIndex={0}
    >
      <Grid container direction="column">
        <Grid
          item
          container
          justifyContent={'space-between'}
          className={classes.headingContainer}
        >
          <Grid item className={classes.headingWidth}>
            <Tooltip title={labelName} classes={tooltip}>
              <Typography
                className={classes.typography}
                variant="h6"
                data-testid="oef-chart-title"
              >
                {labelName}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item>
            {show && (
              <IconButton
                aria-label={isTiwVpatEnabled ? removeButton : 'remove'}
                onClick={handleClickRemove}
                data-testid={`remove-${labelName}-${labelId}`}
                className={classes.removeBtn}
                color="secondary"
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item>
          {loading ? (
            <LabelChartSkeleton />
          ) : isEmpty ? (
            <LabelNoData />
          ) : (
            <PieChart
              data={dataset}
              labelType={labelName}
              setGetCurrentPage={setGetCurrentPage}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};
export default memo(OEFChart);
